import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { SxProps } from '@mui/material';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth } from '../../../../../auth/auth-provider';
import { localized } from '../../../../../i18n/i18n';
import {
  selectAllFirmwares,
  selectAvailableFirmwares,
} from '../../../../../state/features/admin-device/admin-device-slice';
import { getAllFirmwares, getNewOrEqualFirmwaresForDevice } from '../../../../../state/features/admin-device/operation';
import { DropdownOption } from '../../../default-components/basic-dropdown/basic-search-dropdown';
import { StyledSelect } from '../../styled-select';

const styles: SxProps = {
  marginRight: '20px',
  '& .MuiSelect-icon': {
    fontSize: '50px',
  },
};

interface Props {
  connectId: string;
  selectedFirmware: string;
  onFirmwareSelectCallback: (firmwareVersion: string) => void;
  backupFirmwares?: boolean;
  disabled?: boolean;
}

export const FirmwareSelector: FC<Props> = React.memo(
  ({ connectId, selectedFirmware, onFirmwareSelectCallback, backupFirmwares, disabled }) => {
    const dispatch = useDispatch();
    const { isGlobalAdmin } = useAuth();
    const allFirmwares = useSelector(selectAllFirmwares);
    const availableFirmwares = useSelector(selectAvailableFirmwares);
    const firmwares = backupFirmwares ? allFirmwares : availableFirmwares;
    const [firmwareOptions, setFirmwareOptions] = useState<DropdownOption[]>([]);

    useEffect(() => {
      if (!isGlobalAdmin) return;

      backupFirmwares ? dispatch(getAllFirmwares()) : dispatch(getNewOrEqualFirmwaresForDevice(connectId));
    }, [isGlobalAdmin, dispatch, connectId, backupFirmwares]);

    useEffect(() => {
      const options = firmwares.map((firmware) => ({
        value: firmware.version,
        label: firmware.version,
      }));
      options.sort((a, b) => (a.value < b.value ? -1 : 1));
      setFirmwareOptions(options);
    }, [firmwares, selectedFirmware]);

    const handleFirmwareSelect = useCallback(
      (option: DropdownOption) => {
        onFirmwareSelectCallback(option.value);
      },
      [onFirmwareSelectCallback],
    );

    const selectStyle: SxProps = useMemo(
      () => ({ ...styles, width: backupFirmwares ? '100%' : '50%' }),
      [backupFirmwares],
    );

    return (
      <StyledSelect
        label={localized('FirmwareVersion')}
        value={firmwareOptions.find((o) => o.value === selectedFirmware)}
        options={firmwareOptions}
        height={'70px'}
        handleChange={handleFirmwareSelect}
        whitebackground={true}
        IconComponent={KeyboardArrowDownIcon}
        sx={selectStyle}
        disabled={disabled}
      />
    );
  },
);
